import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { DatePipe } from "@angular/common";
import { FormControl, NonNullableFormBuilder } from "@angular/forms";
import { MatPaginator } from "@angular/material/paginator";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { MatTableDataSource } from "@angular/material/table";
import {
  AccountInvoice,
  AccountInvoiceService
} from "@onsip/common/services/api/resources/accountInvoice/account-invoice.service";
import {
  PrepaidTransaction,
  PrepaidTransactionService
} from "@onsip/common/services/api/resources/prepaidTransaction/prepaid-transaction.service";
import { ActivatedRoute } from "@angular/router";
import { OrganizationService } from "@onsip/common/services/api/resources/organization/organization.service";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom
} from "rxjs/operators";
import jsPDF from "jspdf";
import autoTable, { RowInput } from "jspdf-autotable";
import {
  ManualChargeItem,
  PackageInvoiceItem
} from "@onsip/common/services/api/resources/accountInvoice/account-invoice-item";
import { SentenceCasePipe } from "../../shared/pipes/sentence-case.pipe";
import { currencyFormatter } from "../../shared/helpers/currency-formatter";
import {
  UserAddress,
  UserAddressService
} from "@onsip/common/services/api/resources/userAddress/user-address.service";
import {
  FilterDate,
  PSTNTransaction,
  PlanInvoice,
  Transaction,
  TransactionType
} from "./purchase-history.model";
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../shared/const";
import { AccountService } from "@onsip/common/services/api/resources/account/account.service";
import { Contact } from "@onsip/common/services/api/apiResponse/contact";

@Component({
  selector: "onsip-purchase-history",
  templateUrl: "./purchase-history.component.html",
  styleUrls: ["./purchase-history.component.scss"],
  providers: [DatePipe, SentenceCasePipe],
  standalone: false
})
export class PurchaseHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() userId!: string;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  lessThanMediumBreakpoint = this.breakpointObserver
    .observe([Breakpoints.Small, Breakpoints.XSmall])
    .pipe(map(result => result.matches));
  isMedium$ = this.breakpointObserver
    .observe([Breakpoints.Medium])
    .pipe(map(result => result.matches));

  // variables to set up the form
  pageLength = PAGE_SIZE;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  filterForm = new FormControl();
  monthForm = this.fb.control(<FilterDate>{ month: undefined, year: undefined });
  tableLoaded$ = new BehaviorSubject<boolean>(false);
  displayedColumns = ["date", "description", "amount", "actions"];
  pstnSubtableCols = ["transaction details", "pstn amount"];
  planSubtableCols = ["service", "quantity", "cost/unit", "price"];
  savedDates: { [x: string]: string } = {};
  monthOptions: BehaviorSubject<Array<{ month: number; year: number }>> = new BehaviorSubject<
    Array<{ month: number; year: number }>
  >([]);
  isDataObtained$ = new BehaviorSubject(false);
  months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];
  expandedRows: Array<string> = [];

  // variables for getting data
  today!: string;
  orgCreated!: string;
  orgName!: string;

  dataSource = new MatTableDataSource<Transaction>([]);
  transactionHistoryData: Array<Transaction> = [];
  initializedFilters$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private unsubscriber: Subscription = new Subscription();
  private userAddresses: Array<UserAddress> = [];
  private billingContact!: Contact;

  constructor(
    private accountInvoice: AccountInvoiceService,
    private prepaidTransactions: PrepaidTransactionService,
    private activateRoute: ActivatedRoute,
    private orgService: OrganizationService,
    private breakpointObserver: BreakpointObserver,
    private cdRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private sentenceCasePipe: SentenceCasePipe,
    private fb: NonNullableFormBuilder,
    private userAddressService: UserAddressService,
    private accountService: AccountService
  ) {}

  ngOnInit() {
    // call userAddressBrowse
    this.userAddressService.userAddressBrowseWithAccountId();
    this.unsubscriber.add(
      this.userAddressService.state
        .pipe(
          filter(state => !state.loading),
          map(state => Object.values(state.state))
        )
        .subscribe(userAddresses => {
          this.userAddresses = userAddresses;
        })
    );

    this.accountService.accountRead();
    this.unsubscriber.add(
      this.accountService.accountState.subscribe(accounts => {
        const account = accounts[0];
        this.billingContact = account.contact;
      })
    );

    // set up dates
    this.today = this.getNumericDateFromDateString(new Date().toString());
    this.orgCreated = this.today;

    // set up subscription
    this.initializeFilteredTransactions();

    this.unsubscriber.add(
      this.activateRoute.url.subscribe(() => {
        this.filterForm.setValue(history.state.comingFrom || "all");
      })
    );

    this.unsubscriber.add(
      this.orgService.orgState
        .pipe(
          filter(org => !!org.length),
          distinctUntilChanged((a, b) => a.length !== b.length)
        )
        .subscribe(org => {
          this.dataSource.data = [];
          const orgInfo = org[0];
          this.orgCreated = orgInfo.created.slice(0, orgInfo.created.indexOf("T"));
          this.orgName = orgInfo.contact.organization;
          this.populateMonthOptions();
        })
    );
    this.unsubscriber.add(
      this.breakpointObserver
        .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large])
        .subscribe(result => {
          if (result.breakpoints[Breakpoints.Large]) {
            this.displayedColumns = ["date", "description", "amount", "actions"];
            this.planSubtableCols = ["service", "quantity", "cost/unit", "price"];
          } else if (result.breakpoints[Breakpoints.Medium]) {
            this.displayedColumns = ["date", "description", "amount"];
            this.planSubtableCols = ["service", "quantity", "cost/unit", "price"];
          } else if (result.breakpoints[Breakpoints.Small]) {
            this.displayedColumns = ["date", "description"];
            this.planSubtableCols = ["service", "price"];
          } else if (result.breakpoints[Breakpoints.XSmall]) {
            this.displayedColumns = ["date", "description"];
            this.planSubtableCols = ["service", "price"];
          } else {
            this.displayedColumns = ["date", "description", "amount", "actions"];
            this.planSubtableCols = ["service", "quantity", "cost/unit", "price"];
          }

          this.cdRef.markForCheck();
        })
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  getMainRowData(row: Transaction): Array<any> {
    return [{ date: row.date, description: row.description, amount: row.amount }];
  }

  // functions for nicely formatting table data:

  /**
   * Returns the data to display in an expanded column
   * @param row Any type of transaction
   */
  getRowData(row: Transaction): Array<any> {
    return row.lineItems || [];
  }

  /**
   * Returns the columns for an expanded table
   * @param row Either type of transaction
   */
  getDisplayColumns(row: Transaction): Array<any> {
    return row.type === "pstn" ? this.pstnSubtableCols : this.planSubtableCols;
  }

  /**
   * Returns the key to map a column name to a key in a transaction
   * @param column Name of an expanded table column
   */
  getRowKey(column: string): string {
    switch (column) {
      case "service":
        return "service";
      case "quantity":
        return "quantity";
      case "cost/unit":
        return "costPerUnit";
      case "price":
        return "price";
      case "transaction details":
        return "description";
      case "pstn amount":
        return "pstnAmount";
      default:
        return "empty";
    }
  }

  /**
   * Returns row[column] for an expanded table
   * @param row Either type of transaction
   * @param column A column name
   */
  getRowCellData(row: Transaction, column: string): string {
    const rowKey = this.getRowKey(column);
    return (row as any)[rowKey];
  }

  generateRowPDF(row: Transaction): void {
    const orgName = this.orgName;
    const billingAdrress = this.billingContact;
    const printableDoc = new jsPDF();
    const displayCols =
      row.type === "pstn"
        ? [["Date", "No.", "Description", "Amount"]]
        : [
            this.planSubtableCols.map(col => {
              if (col === "cost/unit") {
                return "Cost/Unit";
              }
              return this.sentenceCasePipe.transform(col);
            })
          ];
    const displayRow = [] as Array<RowInput>;
    this.getRowData(row).forEach(data => {
      if (row.type === "pstn") {
        delete data.empty;
        const pdfDetail = [row.date, row.prepaidTransactionId, ...Object.values(data)];
        displayRow.push(pdfDetail as RowInput);
      } else {
        displayRow.push(Object.values(data) as RowInput);
      }
      if (data.bullets && Array.isArray(data.bullets)) {
        data.bullets.forEach((bullet: any) =>
          displayRow.push([`- ${this.bulletInformation(bullet, row)}`] as any)
        );
      }
    });
    autoTable(printableDoc, {
      theme: "plain",
      head: displayCols,
      body: displayRow,
      foot: [["TOTAL", "", "", row.amount]],
      didDrawPage(data: any) {
        // jsPDF 1.4+ uses getWidth, <1.4 uses .width
        const pageSize = printableDoc.internal.pageSize;
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        printableDoc.setFontSize(12);
        // Header
        let invoiceFor, invoiceDate;
        if (row.type === "plan") {
          invoiceDate = row.date;
          const invoiceNum = row.description.split(" ")[1];
          invoiceFor = `Invoice ${invoiceNum}`;
          // printableDoc.text(`Invoice #: ${invoiceNum}`, 20, 40);
          // printableDoc.text(`Invoice date: ${invoiceDate}`, 20, 60);
        } else if (row.type === "pstn") {
          invoiceFor = "Prepaid Account Statement";
          const actualDate = row.dateYMD.split("-"),
            year = Number(actualDate[0]),
            month = actualDate[1],
            begDate = new Date(year, Number(month) - 1, 1).toDateString().split(" ").slice(1),
            endDate = new Date(year, Number(month), 0).toDateString().split(" ").slice(1),
            [begMonth, begDay, begYear] = begDate,
            [endMonth, endDay, endYear] = endDate;
          invoiceDate = `${begMonth} ${begDay}, ${begYear} thru. ${endMonth} ${endDay}, ${endYear}`;
        }

        // Organizing here
        printableDoc.setFontSize(10);
        // HEADER - left side
        printableDoc.addImage(
          "resources/noJs/onsip-blue.png",
          "PNG",
          data.settings.margin.left,
          15,
          40,
          15
        );
        printableDoc.text("Junction Networks, Inc", data.settings.margin.left, 40);
        // HEADER - right side
        if (invoiceFor) {
          printableDoc.text(`${invoiceFor}`, pageWidth / 2 + 10, 18);
        }
        printableDoc.text(`Company: ${orgName}`, pageWidth / 2 + 10, 26);
        printableDoc.text(`Billing Address: ${billingAdrress.address}`, pageWidth / 2 + 10, 34);
        printableDoc.text(
          `${billingAdrress.city}, ${billingAdrress.state}, ${billingAdrress.zipcode}`,
          pageWidth / 2 + 36,
          42
        );
        printableDoc.text(`Account ID: ${row.accountId}`, pageWidth / 2 + 10, 50);
        printableDoc.text(`Date: ${invoiceDate}`, pageWidth / 2 + 10, 58);

        // Footer
        const str = "www.onsip.com | 1.800.801.3381 | billing@onsip.com";
        printableDoc.text(str, pageWidth / 2, pageHeight - 10, { align: "center" });
      },
      margin: { top: 70 }
    });

    if (row.type === "plan") {
      printableDoc.save(`invoice${row.description.split(" ")[1]}.pdf`);
    } else {
      printableDoc.save(`onsip-prepaid-${row.accountId}.pdf`);
    }
  }

  generateCompletePDF(): void {
    const orgName = this.orgName;
    const billingAdrress = this.billingContact;
    const invoiceDate =
      this.monthForm.value &&
      this.monthForm.value.month &&
      `${this.months[this.monthForm.value.month - 1]} ${this.monthForm.value.year}`;
    const accountId = this.transactionHistoryData[0].accountId;
    const printableDoc = new jsPDF();
    const displayCols = [["Date", "Description", "Amount"]];
    const displayRow = [] as Array<RowInput>;
    this.dataSource.data.forEach(obj =>
      this.getMainRowData(obj).forEach(data => {
        displayRow.push(Object.values(data) as RowInput);
      })
    );
    autoTable(printableDoc, {
      theme: "plain",
      head: displayCols,
      body: displayRow,
      didDrawPage(data: any) {
        // jsPDF 1.4+ uses getWidth, <1.4 uses .width
        const pageSize = printableDoc.internal.pageSize;
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        printableDoc.setFontSize(12);

        // Organizing here
        printableDoc.setFontSize(10);
        // HEADER - left side
        printableDoc.addImage(
          "resources/noJs/onsip-blue.png",
          "PNG",
          data.settings.margin.left,
          15,
          40,
          15
        );
        printableDoc.text("Junction Networks, Inc", data.settings.margin.left, 40);
        // HEADER - right side
        printableDoc.text(`Company: ${orgName}`, pageWidth / 2 + 10, 18);
        printableDoc.text(`Billing Address: ${billingAdrress.address}`, pageWidth / 2 + 10, 26);
        printableDoc.text(
          `${billingAdrress.city}, ${billingAdrress.state}, ${billingAdrress.zipcode}`,
          pageWidth / 2 + 36,
          34
        );
        printableDoc.text(`Account ID: ${accountId}`, pageWidth / 2 + 10, 42);
        if (invoiceDate) {
          printableDoc.text(`Transaction Summary: ${invoiceDate}`, pageWidth / 2 + 10, 50);
        } else {
          printableDoc.text("Transaction Summary", pageWidth / 2 + 10, 50);
        }

        // Footer
        const str = "www.onsip.com | 1.800.801.3381 | billing@onsip.com";
        printableDoc.text(str, pageWidth / 2, pageHeight - 10, { align: "center" });
      },
      margin: { top: 70 }
    });
    printableDoc.save(
      `onsip-invoice-summary${
        invoiceDate ? "-" + invoiceDate.toLocaleLowerCase().replace(" ", "-") : ""
      }.pdf`
    );
  }

  bulletInformation(bullet: any, transaction: Transaction) {
    if (transaction.type === "plan") {
      const showServiceQuantity = (transaction as PlanInvoice).showServiceQuantity;
      if (!showServiceQuantity) {
        return bullet.item;
      }
    }
    return `${bullet.quantity} ${bullet.item}`;
  }

  // functions for formatting data

  private dateSort(date1: string, date2: string): number {
    return new Date(date1) < new Date(date2) ? 1 : -1;
  }

  /**
   * Turn a date string into human english
   * @param dateStr YYYY-MM-DD format
   * @returns [Full month name] [day], [year]
   */
  private getDateStringFromNumericDate(dateStr: string): string {
    return this.datePipe.transform(dateStr) || "";
  }

  /**
   * Turns a date string into a date, then formats that date to YYYY-MM-DD
   * @param str a string representing a date - can be a date string
   */
  private getNumericDateFromDateString(str: string): string {
    return new Date(str).toISOString().slice(0, 10);
  }

  // functions for aggregating and filtering transactions
  private initializeFilteredTransactions(): void {
    this.unsubscriber.add(
      combineLatest({
        formFilter: this.filterForm.valueChanges,
        formMonth: this.monthForm.valueChanges
      })
        .pipe(
          debounceTime(300),
          tap(({ formMonth }) => this.triggerApiCall(formMonth as FilterDate)),
          tap(() => {
            this.isDataObtained$.next(false);
          }),
          switchMap(() => {
            return combineLatest({
              accountInvoiceState: this.accountInvoice.accountState,
              prepaidTransactionsState: this.prepaidTransactions.accountState
            });
          }),
          withLatestFrom(this.filterForm.valueChanges, this.monthForm.valueChanges)
        )
        .subscribe(([{ accountInvoiceState, prepaidTransactionsState }, formFilter, formMonth]) => {
          const invoices = Object.values(accountInvoiceState)
            .filter(invoice => invoice.accountInvoiceId !== "0")
            .map(t => this.accountInvoiceItemToTransaction(t));
          const prepaids = prepaidTransactionsState.map(t => this.prepaidCostToTransaction(t));

          const transactions = (invoices as Array<Transaction>)
            .concat(prepaids as Array<Transaction>)
            .sort((a, b) => this.dateSort(a.date, b.date));
          this.dataSource.data = transactions;
          this.transactionHistoryData = transactions;
          this.filterData(formFilter, formMonth);
          this.isDataObtained$.next(true);
          this.tableLoaded$.next(true);
        })
    );
  }

  private accountInvoiceItemToTransaction(invoice: AccountInvoice): PlanInvoice {
    const lineItems: Array<any> = [];
    let showServiceQuantity = true;

    invoice.itemGroups.forEach(itemGroup => {
      itemGroup.item
        .filter(item => item.quantity !== "0" && item.amount !== "0.00")
        .forEach(item => {
          const lineItem = {
            service: item.description,
            quantity: item.quantity,
            costPerUnit: item.unitPrice ?? currencyFormatter(item.unitPrice),
            price: currencyFormatter(item.amount),
            bullets: new Array<any>()
          };
          if (invoice.type === "ManualCharge") {
            const invoiceItem = item as ManualChargeItem;
            if (invoiceItem.type.includes("UnlimitedBundle")) {
              lineItem.service = `${invoiceItem.bundleQuantity} User Package`;
              const bullets = this.parseManualDescription(invoiceItem.description);
              lineItem.bullets = bullets;
            } else {
              lineItem.service = invoiceItem.description;
            }
            lineItem.quantity = "";
            lineItem.costPerUnit = "";
            lineItem.price = currencyFormatter(item.amount);
          }

          if ((item as PackageInvoiceItem).packageItem?.includedStandardCallingMinutes) {
            // const minutes = (item as PackageInvoiceItem).packageItem.includedStandardCallingMinutes
            //   ?.standardCallingMinutesMonthlyRecurringCharge;
            lineItem.bullets.push({
              item: `Standard Calling Minutes`,
              quantity: ""
            });
          }
          if ((item as PackageInvoiceItem).packageItem?.includedResource) {
            (item as PackageInvoiceItem).packageItem.includedResource.forEach(includedResource =>
              lineItem.bullets.push({
                item: includedResource.resourceDescription,
                quantity: includedResource.quantity
              })
            );
          }
          if ((item as PackageInvoiceItem).packageItem) {
            const packageName = (item as PackageInvoiceItem).packageItem.packageName;
            if (packageName.match(/User-1\.[4567]-(\d+)/) || packageName.match(/User-1\.3-(\d+)/)) {
              showServiceQuantity = showServiceQuantity && false;
            }
          }
          lineItems.push(lineItem);
        });
    });
    invoice.taxItems.forEach(taxItem => {
      lineItems.push({
        service: taxItem.taxTypeDesc,
        quantity: "",
        costPerUnit: "",
        price: `$${taxItem.taxAmount}`
      });
    });
    return {
      accountId: invoice.accountId,
      type: "plan",
      date: this.getDateStringFromNumericDate(invoice.date),
      dateYMD: invoice.date,
      description: "Invoice " + invoice.accountInvoiceId,
      amount: `$${invoice.total}`,
      lineItems,
      showServiceQuantity
    };
  }

  private prepaidCostToTransaction(cost: PrepaidTransaction): PSTNTransaction {
    const { prepaidTransactionType, taxXml, accountId, modified, amount, prepaidTransactionId } =
      cost;
    const description =
      prepaidTransactionType === "7" ? this.getDIDMRCDescription(cost) : cost.description;
    const lineItems: Array<{ description: string; pstnAmount: string }> = [
      {
        description: description,
        pstnAmount: currencyFormatter(parseFloat(cost.amount) - parseFloat(cost.tax))
      }
    ];
    if (taxXml) {
      taxXml.forEach(tax => {
        lineItems.push({
          description: tax.taxTypeDesc,
          pstnAmount: currencyFormatter(tax.taxAmount)
        });
      });
    }
    return {
      accountId,
      type: "pstn",
      dateYMD: modified.slice(0, modified.indexOf("T")),
      date: this.getDateStringFromNumericDate(modified.slice(0, modified.indexOf("T"))),
      description,
      amount: currencyFormatter(amount),
      prepaidTransactionId,
      lineItems
    };
  }

  private populateMonthOptions(): void {
    let currentYear = parseInt(this.orgCreated.split("-")[0]);
    let currentMonth = parseInt(this.orgCreated.split("-")[1]);
    const todayYear = parseInt(this.today.split("-")[0]);
    const todayMonth = parseInt(this.today.split("-")[1]);
    const dates = [];

    while (currentYear <= todayYear) {
      while (currentMonth < 13) {
        dates.unshift({ month: currentMonth, year: currentYear });
        if (currentYear === todayYear && currentMonth === todayMonth) {
          break;
        }
        currentMonth++;
      }
      currentMonth = 1;
      currentYear++;
    }

    this.monthOptions.next(dates);
    this.setCurrentDate();
  }

  private setCurrentDate() {
    const todayYear = parseInt(this.today.split("-")[0]);
    const todayMonth = parseInt(this.today.split("-")[1]);
    const date = this.monthOptions
      .getValue()
      .find(item => item.month === todayMonth && item.year === todayYear);
    date && this.monthForm.setValue(date);
  }

  private get startDate() {
    const date = new Date(
      this.monthForm.value.year as number,
      (this.monthForm.value.month as number) - 1,
      1
    );
    return date.toISOString();
  }

  private get endDate() {
    const date = new Date(
      this.monthForm.value.year as number,
      this.monthForm.value.month as number,
      0
    );
    return date.toISOString();
  }

  private parseManualDescription(desc: string): Array<{ item: string; quantity: string }> {
    // this is an html string for a list. We will parse it to an array for the frontend
    const removeUl = desc.replace("<ul>", "").replace("</ul>", "");
    const removeLi = removeUl.replace(/<li>/g, "");
    // split remaining code using </li> and remove any empty strings
    const items = removeLi.split("</li>").filter(item => item !== "");
    return items.map(item => ({ item, quantity: "" }));
  }

  private triggerApiCall(formMonth: FilterDate) {
    const startDate = formMonth.month && formMonth.year ? this.startDate : this.orgCreated;
    //orgCreated - all time;
    if (this.savedDates[startDate] || this.savedDates[this.orgCreated]) {
      return;
    }

    const currentTime = new Date().toISOString();
    const endDate = formMonth.month && formMonth.year ? this.endDate : currentTime;
    this.savedDates[startDate] = startDate;

    this.accountInvoice.accountInvoiceBrowse(startDate, endDate);
    this.prepaidTransactions.prepaidTransactionBrowse({
      startDateTime: startDate,
      endDateTime: endDate
    });
  }

  private filterData(formFilter: TransactionType, formMonth: FilterDate) {
    const transactionMatchesMonth = (transactionDate: string) => {
      const dateArr = this.getNumericDateFromDateString(transactionDate).split("-");
      return formMonth.month === parseInt(dateArr[1]) && formMonth.year === parseInt(dateArr[0]);
    };
    const filtered = this.transactionHistoryData.filter(
      transaction =>
        (transaction.type === formFilter || formFilter === "all") &&
        (!formMonth || !formMonth.month || transactionMatchesMonth(transaction.date))
    );

    this.dataSource.data = filtered;
  }

  trackById(index: number, item: Transaction): string {
    return item.accountId;
  }

  private getDIDMRCDescription(cost: PrepaidTransaction): string {
    const username = this.userAddresses.find(user => user.userId === cost.userId)?.name;
    const quality = cost.description.slice(cost.description.indexOf("(quantity"));
    return `DID MRC for User ID ${cost.userId}${
      username ? " - " + username + " -" : ""
    } ${quality}`;
  }
}
